import { useCallback, useState } from 'react';

/**
 * Toggleable grid columns, in render order. The selection checkbox, the product Name (primary
 * identifier), and the row-actions menu are always visible and intentionally NOT part of this set.
 *
 * The first block mirrors the originally-shipped columns (visible by default); the second block
 * are extra columns ported from the legacy admin grid that stay hidden until enabled via the
 * column selector (`defaultVisible: false`).
 */
/**
 * `sortSelector` is the backend sort field (a ProductGridProjection property name, matched
 * case-insensitively by the `/v1/Products/GetAllGridV2` endpoint). Server-side sort over the
 * 378k-row catalog is only enabled on INDEXED columns (SKU/Code, Barcode) — sorting the computed
 * projection (Name via ML COALESCE, prices/stock/category via correlated subqueries) takes
 * 4–18s and holds a DB connection, so those columns are intentionally left non-sortable. Fast
 * Name/price sort is a follow-up requiring DB indexes / denormalisation. The always-on Name
 * column and every other column render plain (no sortSelector).
 */
export const PRODUCT_COLUMNS = [
	{ key: 'image' },
	{ key: 'sku', sortSelector: 'Code' },
	{ key: 'barcode', sortSelector: 'Barcode' },
	{ key: 'category' },
	{ key: 'stock' },
	// Sale columns render "—" for nearly the whole catalog — hidden by default, recoverable
	// via the column selector (stored visibility overrides this for existing users).
	{ key: 'preDiscount', defaultVisible: false },
	{ key: 'discount', defaultVisible: false },
	{ key: 'retail' },
	{ key: 'visible' },
	// Extra (default-hidden) columns ported from the legacy admin Products grid.
	{ key: 'id', defaultVisible: false, sortSelector: 'Id' },
	{ key: 'subTitle', defaultVisible: false },
	{ key: 'baseName', defaultVisible: false },
	{ key: 'altCode', defaultVisible: false },
	{ key: 'groupCode', defaultVisible: false },
	{ key: 'erpCode', defaultVisible: false },
	{ key: 'manufacturer', defaultVisible: false },
	{ key: 'subType', defaultVisible: false },
	{ key: 'productType', defaultVisible: false },
	{ key: 'availability', defaultVisible: false },
	{ key: 'featured', defaultVisible: false },
	{ key: 'hasDiscount', defaultVisible: false },
	{ key: 'releaseDate', defaultVisible: false },
	{ key: 'sortIndex', defaultVisible: false }
] as const satisfies readonly { key: string; defaultVisible?: boolean; sortSelector?: string }[];

export const PRODUCT_COLUMN_KEYS = PRODUCT_COLUMNS.map(c => c.key);

/** Backend sort selectors — only the fast indexed columns (see PRODUCT_COLUMNS note). */
export const PRODUCT_SORT_SELECTORS: Record<string, string> = Object.fromEntries(
	PRODUCT_COLUMNS.flatMap(c => ('sortSelector' in c ? [[c.key, c.sortSelector]] : []))
);

export type ProductColumnKey = (typeof PRODUCT_COLUMNS)[number]['key'];

export type ColumnVisibility = Record<ProductColumnKey, boolean>;

const STORAGE_KEY = 'admin-v2:products:columns';

function defaultVisibility(): ColumnVisibility {
	return PRODUCT_COLUMNS.reduce((acc, c) => {
		acc[c.key] = (c as { defaultVisible?: boolean }).defaultVisible !== false;
		return acc;
	}, {} as ColumnVisibility);
}

function readStored(): ColumnVisibility {
	const base = defaultVisibility();
	try {
		const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
		if (!raw) return base;
		const parsed = JSON.parse(raw) as Partial<Record<string, boolean>>;
		for (const key of PRODUCT_COLUMN_KEYS) {
			if (typeof parsed[key] === 'boolean') base[key] = parsed[key] as boolean;
		}
	} catch {
		// Corrupt/blocked storage → fall back to per-column defaults.
	}
	return base;
}

function persist(value: ColumnVisibility): void {
	try {
		globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(value));
	} catch {
		// Storage unavailable (private mode / quota) — visibility stays in-memory only.
	}
}

/**
 * Frontend-only column visibility, persisted to localStorage. Returns the current map plus
 * a toggle and reset. Used by the toolbar "Columns" popover and the grid table renderer.
 */
export function useColumnVisibility() {
	const [visible, setVisible] = useState<ColumnVisibility>(readStored);

	const toggle = useCallback((key: ProductColumnKey) => {
		setVisible(prev => {
			const next = { ...prev, [key]: !prev[key] };
			persist(next);
			return next;
		});
	}, []);

	const reset = useCallback(() => {
		const next = defaultVisibility();
		persist(next);
		setVisible(next);
	}, []);

	return { visible, toggle, reset };
}
